import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CONFIG, goToLeadForm } from "@/config";

export const Reveal = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const SectionLabel = ({ children, dark = false, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] ${
      dark ? "text-gold" : "text-royal"
    }`}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${dark ? "bg-gold" : "bg-royal"}`} />
    {children}
  </span>
);

// Primary gold pill CTA -> scrolls to lead form (or navigates to /contact)
export const PrimaryCTA = ({ children = "Get Your Personalised Financial Plan", className = "", testId, onClick }) => {
  const navigate = useNavigate();
  return (
    <button
      data-testid={testId}
      onClick={onClick || (() => goToLeadForm(navigate))}
      className={`group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-4 text-[15px] font-bold text-navy shadow-gold transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#ffc41f] focus:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
};

// Secondary -> WhatsApp expert
export const ExpertCTA = ({ children = "Talk to an Expert", dark = false, className = "", testId, href }) => (
  <a
    data-testid={testId}
    href={href || CONFIG.whatsappUrl}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center justify-center gap-2 rounded-full border px-7 py-4 text-[15px] font-semibold transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
      dark
        ? "border-white/25 text-white hover:bg-white/10 focus-visible:ring-white/50"
        : "border-navy/15 text-navy hover:border-navy/40 hover:bg-navy/[0.03] focus-visible:ring-navy/30"
    } ${className}`}
  >
    {children}
  </a>
);

// Inner-page hero used by About / Services / Learn / Calculators / Contact
export const PageHero = ({ label, title, highlight, lead, children, testId }) => (
  <section className="relative overflow-hidden bg-lightbg pt-28 sm:pt-32 lg:pt-36">
    <div className="pointer-events-none absolute -right-40 -top-24 h-[480px] w-[480px] rounded-full bg-royal/[0.07] blur-3xl" />
    <div className="pointer-events-none absolute -left-32 top-40 h-[320px] w-[320px] rounded-full bg-gold/[0.10] blur-3xl" />
    <div className="relative mx-auto max-w-7xl px-5 pb-14 sm:px-8 lg:pb-20">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <SectionLabel testId={testId}>{label}</SectionLabel>
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl"
      >
        {title} {highlight && <span className="text-royal">{highlight}</span>}
      </motion.h1>
      {lead && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-6 max-w-2xl text-base leading-relaxed text-ink/70 sm:text-lg"
        >
          {lead}
        </motion.p>
      )}
      {children && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.4 }} className="mt-8">
          {children}
        </motion.div>
      )}
    </div>
  </section>
);
